import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, BookOpen } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { useSeo } from "@/lib/useSeo";

const TABS = [["", "Tous"], ["conseil", "Conseils"], ["local", "Zones d'intervention"]];

export default function Conseils() {
  useSeo("Conseils & articles", "Articles, conseils d'experts et guides sur la maçonnerie, le béton et les aménagements extérieurs par SMT Provence.");
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("");
  useEffect(() => {
    Promise.all([
      api.get("/articles", { params: { type: "conseil" } }),
      api.get("/articles", { params: { type: "local" } }),
    ]).then(([a, b]) => setItems([...a.data, ...b.data])).catch(() => {});
  }, []);

  const filtered = tab ? items.filter((a) => a.type === tab) : items;
  const featured = tab === "" ? filtered[0] : null;
  const rest = featured ? filtered.slice(1) : filtered;
  const kind = (a) => (a.type === "local" ? "Zone d'intervention" : "Conseil");

  const Thumb = ({ a, className }) => a.cover_image
    ? <img src={fileUrl(a.cover_image)} alt={a.title} className={`object-cover ${className}`} />
    : <div className={`bg-ink flex items-center justify-center ${className}`}>{a.type === "local" ? <MapPin size={40} className="text-primary" /> : <BookOpen size={40} className="text-primary" />}</div>;

  return (
    <div>
      <section className="pt-32 pb-12 bg-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white">Conseils & articles</h1>
          <p className="mt-4 text-white/70 max-w-2xl">Nos experts partagent leurs conseils et répondent à vos questions sur la maçonnerie et les aménagements extérieurs.</p>
        </div>
      </section>

      <div className="sticky top-[72px] z-30 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap gap-2">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${tab === v ? "bg-primary text-white border-primary" : "bg-white text-ink border-border hover:border-primary"}`}>{l}</button>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {featured && (
          <Reveal>
            <Link to={`/conseils/${featured.slug}`} data-testid={`article-${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-14">
              <div className="overflow-hidden rounded-3xl aspect-[16/10]"><Thumb a={featured} className="w-full h-full group-hover:scale-105 transition-transform duration-700" /></div>
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-black text-ink group-hover:text-primary transition-colors">{featured.title}</h2>
                <p className="mt-2 text-sm font-semibold text-primary">{kind(featured)}</p>
                <p className="mt-4 text-ink/70 leading-relaxed line-clamp-3">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 bg-ink text-white px-6 py-3 rounded-full font-bold group-hover:bg-primary transition">Lire l'article <ArrowRight size={18} /></span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.08}>
              <Link to={`/conseils/${a.slug}`} data-testid={`article-${a.slug}`} className="group flex flex-col h-full bg-card border border-border rounded-2xl overflow-hidden hover:border-primary hover:-translate-y-1 transition-all duration-300">
                <div className="relative overflow-hidden aspect-[16/9]">
                  <Thumb a={a} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[11px] font-bold text-ink">{kind(a)}</div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-heading text-lg font-black text-ink group-hover:text-primary transition-colors line-clamp-2">{a.title}</h3>
                  <p className="mt-2 text-sm text-ink/60 line-clamp-3 flex-1">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-primary text-sm font-bold">Lire <ArrowUpRight size={15} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-ink/50 text-center py-16">Aucun article pour le moment.</p>}
      </section>
    </div>
  );
}
